import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useCanvasWidthPercentage } from 'redux/hooks/common';
import { GetUnifiedExperimentCanvasWidth } from 'utils/Global';
import { useCanvasRectangleSize, useGenerateVirtualPlateWellsAndDetails } from './hooks';
import { RMRectangle } from 'business/models/CustomModels';
import { useRMDispatch } from 'redux/hooks';
import {
  SearchResultState,
  setSelectedRowIndices,
} from 'redux/reducers/searchnFilterReducer';
import { useSearchResult } from 'redux/hooks/searchnFilter';
import { WellSelectionMouseEvents } from 'components/canvas/constants';
import WellSelection from 'components/canvas/wellSelection/wellSelection';
import { useCanvasZoomAndScroll } from 'components/canvas';
import './styles.scss';
import ExtendedCanvas from './extendedCanvas';
import { WellIng } from 'components/Experiment/constants';
import { snfCanvasPadding } from './constants';
import Konva from 'konva';
import { useStore } from 'react-redux';
import { WellIngDTO } from 'business/models/Models';
import { useNavigate } from 'react-router-dom';

export interface WithCanvasContainerProps {
  wellRectangles: RMRectangle[];
  containerRows: number;
  containerCols: number;
  plateHeight: number;
  plateWidth: number;
  wellRects: RMRectangle[];
  wellRectsPadded: RMRectangle[];
  wellWithIngredients: WellIng[];
  canvasRefs: any;
  zoomLevel: number;
  searchResult: SearchResultState;
  setZoomInvocker: (functionDelegate: Dispatch<SetStateAction<number>>) => void;
  onCanvasMouseUp?: (
    evt: Konva.KonvaEventObject<MouseEvent>,
    wellWithIngredients: WellIngDTO[],
  ) => void;
}
const initialZoomValue = 1;
/**
 * Higher-order component that wraps a component with canvas container functionality.
 *
 * @param {React.FC<P>} WrappComponent - The component to be wrapped
 * @param {string} canvasId - The id of the canvas
 * @return {React.FC} The wrapped component with canvas container functionality
 */
const withCanvasContainer = <
  P extends WithCanvasContainerProps = WithCanvasContainerProps,
>(
  WrappComponent: React.FC<P>,
  canvasId: string,
) => {
  const ContainerComponent = (props: any) => {
    const { setZoomInvocker, onCanvasMouseUp } = props;
    const canvasWidth = GetUnifiedExperimentCanvasWidth(useCanvasWidthPercentage());
    const [canvasRefs, setCanvasRefs] = useState({
      stageRef: undefined,
      wellSelectionLayer: undefined,
      layerRef: undefined,
    });
    const container = useRef(null);
    const searchResult = useSearchResult();
    const [zoomScrollHandler, zoomAndScrollToFocusAreaHandler, scale] =
      useCanvasZoomAndScroll(canvasRefs.stageRef, container.current, initialZoomValue);
    const [wellSelectionMouseEvents, setWellSelectionMouseEvents] =
      useState<WellSelectionMouseEvents>({
        onMouseDown: () => {},
        onMouseMove: () => {},
        getWellSelectionDetails: () => ({
          selectedWellNumbers: [],
          appendSelection: false,
        }),
      });
    const vPlateAndWellDetails = useGenerateVirtualPlateWellsAndDetails();
    const dispatch = useRMDispatch();
    const canvasRectangleSize = useCanvasRectangleSize();
    const store = useStore();
    const navigate = useNavigate();
    const allWellsWithIngredients = useMemo(() => {
      const { searchResult, wellDrops } = store.getState()?.searchnFilter || {};
      if (!searchResult || !wellDrops) {
        return [];
      }
      return searchResult?.rows?.items?.map((w) =>
        wellDrops?.data?.items?.find((x) =>
          x.dropList.some((d) => d.dropId == w.__WellDropID),
        ),
      );
    }, [
      store.getState()?.searchnFilter?.searchResult?.rows?.items,
      store.getState()?.searchnFilter?.wellDrops?.data?.items,
    ]);
    useEffect(() => {
      if (!container.current || !canvasRefs.stageRef) {
        return;
      }

      const scrollContainer = container.current;
      const canvasStage = canvasRefs.stageRef;

      const moveStage = function moveStage() {
        const dx = scrollContainer.scrollLeft - snfCanvasPadding;
        const dy = scrollContainer.scrollTop - snfCanvasPadding;

        canvasStage.container().style.transform = `translate(${dx}px, ${dy}px)`;
        canvasStage.x(-dx);
        canvasStage.y(-dy);
        canvasStage.batchDraw();
      };

      scrollContainer.addEventListener('scroll', moveStage);
      moveStage();

      return () => {
        scrollContainer.removeEventListener('scroll', moveStage);
        canvasRefs.stageRef.current = null;
        container.current = null;
        canvasRefs.wellSelectionLayer.current = null;
        canvasRefs.layerRef.current = null;
      };
    }, [canvasRefs.stageRef, container.current]);

    useEffect(() => {
      setZoomInvocker(zoomScrollHandler);
    }, [canvasRefs.stageRef, container.current, scale]);

    const {
      containerRows,
      containerCols,
      plateHeight,
      plateWidth,
      wellWithIngredients,
      wellRects,
    } = vPlateAndWellDetails;
    const onMouseDownHandler = (evt: Konva.KonvaEventObject<MouseEvent>) => {
      wellSelectionMouseEvents.onMouseDown(evt);
    };

    const onMouseMoveHandler = (evt) => {
      wellSelectionMouseEvents.onMouseMove(evt);
    };
    const onMouseUpHandler = (evt) => {
      const { selectedWellNumbers } =
        wellSelectionMouseEvents.getWellSelectionDetails(evt) || {};

      if (
        selectedWellNumbers?.length &&
        Math.max(...selectedWellNumbers) <= searchResult.rows.items.length
      ) {
        const isCtrlPressed = evt?.evt?.ctrlKey || evt?.evt?.metaKey;
        let newSelectedIndices = isCtrlPressed
          ? [...searchResult.selectedRowIndices]
          : [];

        selectedWellNumbers.forEach((w) => {
          const idx = w - 1;
          const exists = newSelectedIndices.includes(idx);

          if (isCtrlPressed) {
            if (exists) {
              newSelectedIndices = newSelectedIndices.filter((i) => i !== idx);
            } else {
              newSelectedIndices.push(idx);
            }
          } else {
            newSelectedIndices.push(idx);
          }
        });

        dispatch(setSelectedRowIndices(newSelectedIndices));
      }
      if (onCanvasMouseUp) {
        onCanvasMouseUp(evt, allWellsWithIngredients);
      }
    };

    const onDblClickHandler = () => {
      navigate(`drop`);
    };

    const selectedWellCoordinates = useMemo(() => {
      if (!wellRects?.length || searchResult?.selectedRowIndices.length < 1) {
        return [];
      }

      return searchResult.selectedRowIndices.map((i) => wellRects[i]);
    }, [searchResult.selectedRowIndices, wellRects]);

    if (!wellWithIngredients?.length || !wellRects) {
      return null;
    }

    const wellSelectionContent = (
      <WellSelection
        canvasRefs={canvasRefs}
        containerRows={containerRows}
        containerCols={containerCols}
        wellRects={wellRects}
        selectedWellCoordinates={selectedWellCoordinates}
        setWellSelectionMouseEvents={setWellSelectionMouseEvents}
        canvasRectangleSize={canvasRectangleSize}
      />
    );

    const canvasScrollWidth = canvasWidth * scale;
    const canvasScrollHeight = plateHeight * scale;
    const scrollDimensions = {
      width: canvasScrollWidth + snfCanvasPadding * 2,
      height: canvasScrollHeight + snfCanvasPadding * 2,
      overflow: 'hidden',
      minHeight: '100%',
    };

    return (
      <div id="canvas-scroll-container" ref={container}>
        <div id="large-container" style={scrollDimensions}>
          <ExtendedCanvas
            canvasWidth={canvasWidth + snfCanvasPadding * scale}
            canvasHeight={plateHeight + snfCanvasPadding * scale}
            height={plateHeight + snfCanvasPadding * scale}
            width={plateWidth}
            cols={containerCols}
            rows={containerRows}
            canvasStageContainerId={canvasId}
            stageRef={(stageRef, layerRef, drawingLayerRef) =>
              setCanvasRefs({
                stageRef: stageRef,
                wellSelectionLayer: drawingLayerRef,
                layerRef: layerRef,
              })
            }
            onMouseDown={onMouseDownHandler}
            onMouseMove={onMouseMoveHandler}
            onWheel={zoomScrollHandler}
            onMouseUp={onMouseUpHandler}
            onDblClick={onDblClickHandler}
            initialStageScale={scale}
            wellSelectionContent={wellSelectionContent}
            handleZoomAreaSelected={zoomAndScrollToFocusAreaHandler}
          >
            {canvasRefs.stageRef && (
              <WrappComponent
                {...(props as P)}
                {...vPlateAndWellDetails}
                canvasRefs={canvasRefs}
                zoomLevel={1}
                searchResult={searchResult}
              />
            )}
          </ExtendedCanvas>
        </div>
      </div>
    );
  };
  return ContainerComponent;
};

export default withCanvasContainer;
